// Fetches the list of games hydra knows about for the dates currently on the
// board — one day in the default view, a whole slate window in #712's weekly
// mode — from the analytics endpoint (Postgres-backed, deeper history than the WS cache).
// Returns one entry per game with per-source trade volume — the only
// numeric data point the skinny archived row in GameTable renders.
//
// Why this view consumes analytics: g1's live WS feed is the source of
// truth for live state, but hydra prunes finalized markets from the WS
// cache ~5 min after close. To show closed games on the dashboard
// (especially when the user picks yesterday's date) we have to read
// from the analytics surface. This is a deliberate per-view carve-out
// — see [[feedback_g1_live_feed_only]] and [[project_game_detail_page]]
// for the same pattern in GameDetail. The deeper "with prices" surface
// is tracked in [[project_hydra_ask_analytics_games_with_prices]]; until
// that ships, the dashboard renders volume-only skinny rows.
//
// Dedupe-against-live happens in the caller (ValueDashboard), not here:
// this hook knows nothing about marketStore, returns every game hydra
// has for the date, and the caller drops anything still ingested live.
//
// Performance:
//   - Module-level cache by dateIso so revisiting a date is instant.
//     No TTL — settled historical dates don't change, and today's
//     slight staleness within a session is fine.
//   - In-flight promise dedupe so concurrent mounts (or preloads
//     racing the foreground fetch) only hit hydra once per date.
//   - Adjacent-date preload (±1 day around the window) on change,
//     fire-and-forget into the cache. Makes left/right navigation feel
//     instant.

import { useEffect, useState } from 'react';
import {
  getGames as getAnalyticsGames,
  hasAnalytics,
  type GameSummary,
} from '@/services/hydraAnalytics';

// Empirical: hydra's analytics surface only returns rows for the last
// ~7 days even though price_ticks is documented as 30-day retention.
// Either the purge runs more aggressively or the docs are stale; either
// way, asking for older dates returns empty so we just cap the window
// here and the date picker effectively can't go further back.
const MAX_LOOKBACK_DAYS = 7;
const DAY_MS = 86_400_000;

const cache = new Map<string, ArchivedGame[]>();
const inflight = new Map<string, Promise<ArchivedGame[]>>();

export interface ArchivedGame {
  /** Hydra canonical: "YYYY-MM-DD|TEAM_A|TEAM_B" (alpha-sorted). Use as
   *  the dedupe key against marketStore-derived GameRow.key via
   *  toHydraGameKey. */
  gameKey: string;
  date: string;
  sport: string;
  /** Alpha-sorted to match canonicalGameKey. Not necessarily away/home;
   *  the skinny row just renders "TEAM_A @ TEAM_B" — there's no per-side
   *  data to attach so the actual game-day order doesn't matter. */
  teamA: string;
  teamB: string;
  kalshiVolume: number;
  polyVolume: number;
}

export interface UseArchivedGamesResult {
  archived: ArchivedGame[];
  loading: boolean;
  error: Error | null;
}

/**
 * Archived rows for every date in the bounded view, in the order given.
 *
 * Takes the whole window rather than one date (#712): a weekly slate claims an
 * inclusive range, so reading closed games for only its anchor day would show
 * one day out of seven while the toolbar named all of them. Pass null (or an
 * empty list) to read nothing.
 */
export function useArchivedGames(dateIsos: readonly string[] | null): UseArchivedGamesResult {
  // One stable dependency for the window. The caller derives its date list per
  // render, so depending on the array itself would re-run the effect on every
  // feed tick.
  const dateKey = dateIsos ? dateIsos.join(',') : '';
  const [state, setState] = useState<UseArchivedGamesResult>(() => ({
    archived: cachedRowsFor(fetchableDates(splitDateKey(dateKey))),
    loading: false,
    error: null,
  }));

  useEffect(() => {
    // Dates with nothing to fetch — future days, days past the analytics
    // lookback — drop out here, so a window straddling the lookback edge still
    // renders the part of itself that exists.
    const dates = fetchableDates(splitDateKey(dateKey));
    if (dates.length === 0) {
      setState({ archived: [], loading: false, error: null });
      return;
    }

    // Every date already cached — render synchronously, no fetch and none of
    // the clear-then-refill flicker below.
    if (dates.every((d) => cache.has(d))) {
      setState({ archived: cachedRowsFor(dates), loading: false, error: null });
      preloadAdjacent(dates);
      return;
    }

    let cancelled = false;
    // Clear stale data immediately so the dashboard doesn't render the
    // previous window's archived rows during the seconds it takes for the
    // analytics fetches to land — that flicker looked like a slow filter.
    setState({ archived: [], loading: true, error: null });

    // One request per date, deduped and cached per date by fetchAndCache, so a
    // window costs at most its own length once and nothing on the way back.
    Promise.all(dates.map((d) => fetchAndCache(d)))
      .then((perDate) => {
        if (cancelled) return;
        setState({ archived: perDate.flat(), loading: false, error: null });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if ((err as { name?: string })?.name === 'AbortError') return;
        // Same all-or-nothing semantics a single date has always had: a failed
        // read shows no archived rows rather than a silently partial window.
        setState({ archived: [], loading: false, error: err as Error });
      });

    preloadAdjacent(dates);

    return () => {
      cancelled = true;
      // Don't abort the underlying fetches — their results still benefit the
      // cache for the next mount on these dates. Only the local setState
      // is cancelled.
    };
  }, [dateKey]);

  return state;
}

function splitDateKey(dateKey: string): string[] {
  return dateKey ? dateKey.split(',') : [];
}

/** The dates worth asking analytics about: it has no record of games that
 *  haven't happened, and none older than its lookback window. */
function fetchableDates(dates: readonly string[]): string[] {
  if (!hasAnalytics()) return [];
  return dates.filter(
    (d) => !isFutureDate(d) && !isOutsideLookback(d, MAX_LOOKBACK_DAYS),
  );
}

function cachedRowsFor(dates: readonly string[]): ArchivedGame[] {
  return dates.flatMap((d) => cache.get(d) ?? []);
}

function fetchAndCache(dateIso: string): Promise<ArchivedGame[]> {
  // Dedupe concurrent requests for the same date — multiple mounts (or
  // preload racing the foreground fetch) share one upstream request.
  const existing = inflight.get(dateIso);
  if (existing) return existing;

  const promise = getAnalyticsGames({ date: dateIso })
    .then((res) => {
      const archived = res.items
        .map(toArchivedGame)
        .filter((g): g is ArchivedGame => g !== null);
      cache.set(dateIso, archived);
      return archived;
    })
    .finally(() => {
      inflight.delete(dateIso);
    });

  inflight.set(dateIso, promise);
  return promise;
}

/**
 * Warm the day either side of the window so stepping to it feels instant.
 *
 * Deliberately one day per side, not a whole adjacent window: the analytics
 * surface only answers for roughly MAX_LOOKBACK_DAYS back, so a week's
 * PREVIOUS window sits entirely outside the lookback and its NEXT window is
 * entirely in the future — both no-ops. Preloading fourteen dates to discover
 * that would cost real requests to learn nothing.
 */
function preloadAdjacent(dates: readonly string[]): void {
  if (!hasAnalytics() || dates.length === 0) return;
  const edges: Array<[string, number]> = [
    [dates[0], -1],
    [dates[dates.length - 1], 1],
  ];
  for (const [edge, offset] of edges) {
    const adjacent = shiftDate(edge, offset);
    if (!adjacent) continue;
    if (cache.has(adjacent) || inflight.has(adjacent)) continue;
    if (isOutsideLookback(adjacent, MAX_LOOKBACK_DAYS)) continue;
    // Future dates are pointless to preload — analytics won't have rows
    // for games that haven't happened yet.
    if (isFutureDate(adjacent)) continue;
    fetchAndCache(adjacent).catch(() => {
      // Background fetch — failure is silent; the foreground hook will
      // surface real errors when the user actually navigates there.
    });
  }
}

function shiftDate(dateIso: string, days: number): string | null {
  const ms = new Date(`${dateIso}T00:00:00`).getTime();
  if (!Number.isFinite(ms)) return null;
  const d = new Date(ms + days * DAY_MS);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function toArchivedGame(g: GameSummary): ArchivedGame | null {
  if (!g.gameKey || !g.teams || g.teams.length < 2) return null;
  const [teamA, teamB] = [g.teams[0], g.teams[1]].map((t) => t.toUpperCase()).sort();
  let kalshiVolume = 0;
  let polyVolume = 0;
  for (const t of g.trades ?? []) {
    if (t.source === 'kalshi') kalshiVolume += t.totalSize;
    else if (t.source === 'polymarket') polyVolume += t.totalSize;
  }
  return {
    gameKey: g.gameKey,
    date: g.date,
    sport: (g.sport ?? '').toLowerCase(),
    teamA,
    teamB,
    kalshiVolume,
    polyVolume,
  };
}

function isOutsideLookback(dateIso: string, days: number): boolean {
  const target = new Date(`${dateIso}T00:00:00`).getTime();
  if (!Number.isFinite(target)) return true;
  return target < Date.now() - days * DAY_MS;
}

// True when the dateIso refers to a calendar day after today (local
// time). Lexicographic compare on YYYY-MM-DD works because the format
// is monotonic. selectedDate from the dashboard is local-time mdy →
// iso so this stays consistent with what the user sees in the picker.
function isFutureDate(dateIso: string): boolean {
  const now = new Date();
  const todayIso = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  return dateIso > todayIso;
}
